import {
  ApolloProvider,
  ApolloLink,
  Resolvers,
  PossibleTypesMap,
  TypePolicies,
  IdGetter,
  NormalizedCacheObject,
  ApolloClient,
} from '@apollo/client'
import React, { createContext, useContext, ReactChild } from 'react'

import createClient from './createClient'

const graphqlUri = process.env.REACT_APP_APOLLO_GRAPHQL_URL

export const LoginPageContext = createContext('/account/login')
export const LogoutPageContext = createContext('/account/logout')

export type AuthPage = ({ default: string } & Record<string, string>) | string
export interface AuthPageContextProps {
  login: AuthPage
  logout: AuthPage
}
export const AuthPageContext = createContext<AuthPageContextProps | undefined>(
  undefined,
)

interface Props {
  children: ReactChild
  resolvers?: Resolvers[]
  possibleTypes?: PossibleTypesMap
  typePolicies?: TypePolicies
  dataIdFromObject?: IdGetter
  links?: ApolloLink[]
  storeView: string
  restoreData?: NormalizedCacheObject
}

// There should only be one client in use.
// It isn't 100% guaranteed that the correct client is stored in the variable, it may break due to the Apollo Connector being used multiple times etc.
export let client: ApolloClient<unknown> | undefined = undefined

// Reset or overwrite the global client
export function setApolloClient(
  apolloClient: ApolloClient<unknown> | undefined,
) {
  client = apolloClient
}

const ApolloConnector = ({
  children,
  dataIdFromObject,
  resolvers,
  possibleTypes,
  typePolicies,
  links,
  storeView,
  restoreData,
}: Props) => {
  if (!graphqlUri) {
    throw new Error(
      'Missing environment variable: REACT_APP_APOLLO_GRAPHQL_URL',
    )
  }

  const loginPage = useContext(LoginPageContext)
  const logoutPage = useContext(LogoutPageContext)
  const authPageContext = useContext(AuthPageContext)
  const authPages = authPageContext || {
    login: loginPage,
    logout: logoutPage,
  }

  if (client) {
    return <ApolloProvider client={client}>{children}</ApolloProvider>
  }

  client = createClient({
    uri: graphqlUri,
    storeView,
    cache: {
      possibleTypes,
      typePolicies,
      dataIdFromObject,
    },
    resolvers,
    links,
    authPages,
    restoreData,
  })

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}

export default ApolloConnector
